import Image from 'next/image'
import { CalendarClock } from "lucide-react"
import { Badge } from '@/components/ui/badge'
import { ChatIcon } from '@heroicons/react/outline'
import { MessageList } from './message-list'
import { MMS_MESSAGES, MMS_MESSAGES_FITNESS, ONE_ON_ONE_MESSAGES, ONE_ON_ONE_MESSAGES_FITNESS, VOICEMAIL_MESSAGES, VOICEMAIL_MESSAGES_FITNESS } from '../homepage/constants'
import { MessageProps } from '../homepage/types'

export type Tab = {
  id: string
  title: string
  description: string
  component?: React.FC<{ tab: Tab }>
}
export const FEATURES_TABS: Tab[] = [
  {
    id: 'mass-text-messaging',
    title: 'Mass text messaging',
    description:
      'Harness the power of bulk text messaging: dispatch a message to your entire list or precise segments. Choose to schedule your campaign or send it instantly for immediate impact. Boost engagement and keep the conversation going strong.',
    component: MassTextMessagingContent
  },
  {
    id: 'one-on-one-messaging',
    title: 'One on one messaging',
    description:
      'Helios empowers you to foster one-on-one relationships with your customers. Respond fast, answer questions, or just check-in — all via private messaging. Make the most of a tool that’s fast becoming a must-have in business text communication.',
    component: (props) => <OneOnOneMessagingContent thread={ONE_ON_ONE_MESSAGES} tab={props.tab} />
  },
  {
    id: 'mms-picture-messaging',
    title: 'MMS / Picture messaging',
    description:
      'Bring your messages to life with picture messaging. Easily add photos to your text campaigns to foster greater engagement and achieve higher response rates. Make every message picture-perfect with Helios.',
    component: (props) => <MMSMessagingContent thread={MMS_MESSAGES} tab={props.tab} />
  },
  {
    id: 'voicemail-to-text',
    title: 'Voicemail to text',
    description:
      'Never miss a message with Voicemail to SMS. When a customer calls your Helios number, their voicemail will be transcribed directly to your conversation thread with the call audio. Simply text back within Helios to respond, keeping all your communications streamlined and in one place.',
    component: (props) => <VoicemailMessagingContent thread={VOICEMAIL_MESSAGES} tab={props.tab} />
  }
]
export const FITNESS_TABS: Tab[] = [
  {
    id: 'mass-text-messaging',
    title: 'Mass text messaging',
    description:
      'Maximize your outreach with bulk text messaging. Announce new classes, offers, or health tips to your entire list or target segments. Schedule your fitness campaigns or send instantly to keep your members actively engaged.',
    component: MassTextMessagingContent
  },
  {
    id: 'one-on-one-messaging',
    title: 'One on one messaging',
    description:
      'Strengthen personal connections with your gym members using Helios. Offer personalized fitness advice, schedule training sessions, or provide motivational messages directly, enhancing individual member experiences.',
    component: (props) => <OneOnOneMessagingContent thread={ONE_ON_ONE_MESSAGES_FITNESS} tab={props.tab} />
  },
  {
    id: 'mms-picture-messaging',
    title: 'MMS / Picture messaging',
    description:
      'Energize your text campaigns for your gym with MMS. Share workout visuals, nutrition tips, or event photos to create more engaging and dynamic communications, boosting interaction and response rates.',
    component: (props) => <MMSMessagingContent thread={MMS_MESSAGES_FITNESS} tab={props.tab} />
  },
  {
    id: 'voicemail-to-text',
    title: 'Voicemail to text',
    description:
      'Ensure your gym never misses a member’s call with Voicemail to SMS. Transcribe voicemails to text and respond directly within Helios. Manage all communications smoothly, keeping your gym’s customer service responsive and efficient.',
    component: (props) => <VoicemailMessagingContent thread={VOICEMAIL_MESSAGES_FITNESS} tab={props.tab} />
  }
]
export const TAB_GROUPS: { [key: string]: Tab[] } = {
  fitness: FITNESS_TABS,
  features: FEATURES_TABS

}
export function ActiveMemberSegmentUI() {
  return (
    <div className='z-1 bg-background rounded-xl relative flex-shrink-0 mb-4 overflow-hidden border shadow-md'>
      <div className=' flex items-center justify-between border-b py-2 px-2.5'>
        <div className='flex items-center'>
          <div className='bg-background border-border flex items-center p-1 mr-1 border rounded-lg'>
            <CalendarClock className='w-3.5 h-3.5 shrink-0 text-muted-foreground' />
          </div>
          <p className='md:text-sm ml-1 text-xs font-medium'>Send message tomorrow</p>
        </div>
      </div>
      <ul className='p-2 space-y-2'>
        <li className='text-xs flex items-center rounded-sm px-1.5'>
          <Image
            src='/images/integrations/mindbody/icon.png'
            alt='tablelist sms'
            width={100}
            height={100}
            className='shrink-0 w-4 h-4 mr-2'
          />
          <span className='font-medium'>Active memberships</span>
        </li>
        <li className='flex space-x-1'>
          <Badge variant='outline' className='text-[11px]'>
            gender is female
          </Badge>
        </li>
      </ul>
      <ul className='p-2 space-y-1'>
        <li className='text-xs flex items-start rounded-md px-1.5 md:text-sm'>
          <ChatIcon className='shrink-0 text-muted-foreground w-4 h-4 mr-2' />
          <span>
            Hey <span className='font-mono text-[11px] bg-muted'>{`{{firstName}}`}</span>! We are
            launching a new advanced HIIT workout class and want to give our loyal members first
            dibs on signing up 💪 Reply CLASS and we will save your spot!
          </span>
        </li>
      </ul>
    </div>
  )
}
function MassTextMessagingContent(props: { tab: Tab }) {
  return (
    <>
      <div className='lg:col-span-7'>
        <ActiveMemberSegmentUI />
      </div>
      <div className='lg:col-span-9'>
        <h3 className='text-lg sm:text-xl md:text-2xl font-extrabold text-foreground tracking-tight md:leading-[1.1] text-balance'>
          {props.tab.title}
        </h3>
        <p className='text-muted-foreground text-balance mt-4 leading-normal'>
          {props.tab.description}
        </p>
      </div>
    </>
  )
}
function OneOnOneMessagingContent(props: { tab: Tab, thread: MessageProps[] }) {
  return (
    <>
      <div className='lg:col-span-7'>
        <MessageList messages={props.thread} />
      </div>
      <div className='lg:col-span-9'>
        <h3 className='text-lg sm:text-xl md:text-2xl font-extrabold text-foreground tracking-tight md:leading-[1.1] text-balance'>
          {props.tab.title}
        </h3>
        <p className='text-muted-foreground text-balance mt-4 leading-normal'>
          {props.tab.description}
        </p>
      </div>
    </>
  )
}
function MMSMessagingContent(props: { tab: Tab, thread: MessageProps[] }) {
  return (
    <>
      <div className='lg:col-span-7'>
        <MessageList messages={props.thread} />
      </div>
      <div className='lg:col-span-9'>
        <h3 className='text-lg sm:text-xl md:text-2xl font-extrabold text-foreground tracking-tight md:leading-[1.1] text-balance'>
          {props.tab.title}
        </h3>
        <p className='text-muted-foreground text-balance mt-4 leading-normal'>
          {props.tab.description}
        </p>
      </div>
    </>
  )
}
function VoicemailMessagingContent(props: { tab: Tab, thread: MessageProps[] }) {
  return (
    <>
      <div className='lg:col-span-7'>
        <MessageList messages={props.thread} />
      </div>
      <div className='lg:col-span-9'>
        <h3 className='text-lg sm:text-xl md:text-2xl font-extrabold text-foreground tracking-tight md:leading-[1.1] text-balance'>
          {props.tab.title}
        </h3>
        <p className='text-muted-foreground text-balance mt-4 leading-normal'>
          {props.tab.description}
        </p>
      </div>
    </>
  )
}